import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";
import type { ChecklistSource } from "@/components/audits/NewAuditWizard/types";
import { useWorkspaceId } from "@/hooks";

interface CreateAuditArgs {
  name: string;
  templateCode: string;
  templateId: Id<"auditTemplates">;
  description?: string;
  deadline?: number;
  trigger: string;
  tagIds?: Id<"auditTags">[];
  notes?: string;
  auditorRequestText?: string;
  checklistSource?: ChecklistSource;
  uploadedDocumentStorageId?: Id<"_storage">;
}

interface UseCreateAuditReturn {
  createAudit: (args: CreateAuditArgs) => Promise<Id<"audits">>;
  isCreating: boolean;
}

export function useCreateAudit(): UseCreateAuditReturn {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(api.audit_mutations.createAudit, {
    name: "create-audit",
    successMessage: "Audit created",
  });

  const createAudit = async (args: CreateAuditArgs): Promise<Id<"audits">> => {
    // useApiMutation unwraps the Result pattern at runtime, returning the inner data.
    // TypeScript can't track this unwrapping through the generic types.
    const result = await mutation.mutateAsync({ ...args, workspaceId });
    return result;
  };

  return {
    createAudit,
    isCreating: mutation.isPending,
  };
}
